import { useState } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

// Drag-and-drop image upload with preview. Value is a URL (relative /api/uploads/ or absolute).
export default function ImageUpload({ label, value, onChange, hint, testId, required }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8 MB)");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/admin/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    upload(e.dataTransfer.files?.[0]);
  };

  const resolvedSrc = value && value.startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL || ""}${value}`
    : value;

  return (
    <div className="mb-5">
      <label className="block text-xs uppercase tracking-widest text-[#D8D6D0]/60 mb-2">
        {label}
        {required && <span className="text-[#8C1D30] ml-1">*</span>}
      </label>

      {value ? (
        <div className="relative group" data-testid={testId || "image-upload-preview"}>
          <div className="aspect-[16/10] overflow-hidden bg-[#0d0d0d] border border-[#D8D6D0]/15">
            <img src={resolvedSrc} alt="Preview" className="w-full h-full object-cover" />
          </div>
          <button
            type="button"
            data-testid="image-clear-btn"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 p-1.5 bg-[#151515]/90 border border-[#D8D6D0]/20 hover:bg-[#8C1D30] hover:border-[#8C1D30] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <label className="absolute bottom-2 right-2 cursor-pointer px-3 py-1.5 bg-[#151515]/90 border border-[#D8D6D0]/20 hover:bg-[#1f1f1f] text-xs uppercase tracking-widest">
            Replace
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
            />
          </label>
        </div>
      ) : (
        <label
          data-testid={testId || "image-upload-dropzone"}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`flex flex-col items-center justify-center gap-3 aspect-[16/10] border-2 border-dashed cursor-pointer transition-colors ${
            dragOver
              ? "border-[#8C1D30] bg-[#8C1D30]/5"
              : "border-[#D8D6D0]/15 hover:border-[#D8D6D0]/40 bg-[#0d0d0d]"
          }`}
        >
          {uploading ? (
            <>
              <Upload className="w-7 h-7 text-[#D8D6D0]/40 animate-pulse" />
              <span className="text-sm text-[#D8D6D0]/60">Uploading…</span>
            </>
          ) : (
            <>
              <ImageIcon className="w-7 h-7 text-[#D8D6D0]/30" />
              <div className="text-center px-4">
                <div className="text-sm text-[#D8D6D0]/70">Drop image or click to upload</div>
                <div className="text-[11px] text-[#D8D6D0]/40 mt-1">JPG · PNG · WebP · max 8 MB</div>
              </div>
            </>
          )}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
          />
        </label>
      )}

      {/* Allow paste URL too */}
      <div className="mt-3">
        <input
          type="text"
          placeholder="…or paste image URL"
          value={value && !value.startsWith("/api/") ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#0d0d0d] border border-[#D8D6D0]/15 px-3 py-2 text-xs text-[#F5F3EF]/80 placeholder-[#D8D6D0]/30 focus:outline-none focus:border-[#8C1D30]"
        />
      </div>

      {hint && <p className="text-[11px] text-[#D8D6D0]/40 mt-1.5">{hint}</p>}
    </div>
  );
}
